import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, FileText, Copy, Download, Sparkles, Lock, Briefcase, ExternalLink } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PaywallModal from "@/components/PaywallModal";

export default function CvBuilderPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const isPro = user?.is_admin || (user?.subscription_plan && ["premium", "lifetime_pro", "business"].includes(user.subscription_plan));
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    location: "",
    target_role: "",
    summary: "",
    tone: "professional",
  });
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [experience, setExperience] = useState([{ role: "", company: "", start: "", end: "", bullets: [""] }]);
  const [education, setEducation] = useState([""]);
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    setSkills((arr) => [...arr, s]);
    setSkillInput("");
  };
  const updateExp = (i, k, v) => setExperience((arr) => arr.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const updateBullet = (i, bi, v) => setExperience((arr) => arr.map((e, idx) => idx === i ? { ...e, bullets: e.bullets.map((b, j) => j === bi ? v : b) } : e));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.target_role) {
      return toast.error("Please fill in name, email and target role");
    }
    setLoading(true);
    setOutput(null);
    try {
      const payload = {
        ...form,
        skills,
        experience: experience.filter((x) => x.role).map((x) => ({ ...x, bullets: x.bullets.filter(Boolean) })),
        education: education.filter(Boolean),
      };
      const { data } = await api.post("/documents/cv", payload);
      setOutput(data);
      toast.success("CV generated! +3 points · +£0.05 cashback");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate CV");
    } finally { setLoading(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output.content);
    toast.success("CV copied to clipboard");
  };
  const download = (format = "pdf") => {
    if (!output?.doc_id) return;
    if (!isPro) {
      api.post("/paywall/hit", { kind: "cv_download_attempt", context: output.doc_id }).catch(() => {});
      setPaywallOpen(true);
      return;
    }
    window.open(`${API}/documents/${output.doc_id}/${format}`, "_blank");
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Document Creator · CV</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Build a professional CV</h1>
      <p className="mt-2 text-muted-foreground">Fill the form — AI will produce an ATS-friendly CV you can copy or download.</p>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="cv-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FieldInput label="Full name *" value={form.full_name} onChange={(v) => update("full_name", v)} testid="cv-name" />
            <FieldInput label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} testid="cv-email" />
            <FieldInput label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testid="cv-phone" />
            <FieldInput label="Location" value={form.location} onChange={(v) => update("location", v)} testid="cv-location" />
          </div>
          <FieldInput label="Target role *" value={form.target_role} onChange={(v) => update("target_role", v)} testid="cv-target-role" />
          <FieldTextarea label="Short professional summary" value={form.summary} onChange={(v) => update("summary", v)} testid="cv-summary" />

          <div>
            <Label>Tone</Label>
            <Select value={form.tone} onValueChange={(v) => update("tone", v)}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="cv-tone-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="friendly">Friendly</SelectItem>
                <SelectItem value="bold">Bold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Skills</Label>
            <div className="flex gap-2">
              <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} placeholder="e.g. JavaScript" className="h-11" data-testid="cv-skill-input" />
              <Button type="button" variant="outline" onClick={addSkill} data-testid="cv-add-skill"><Plus className="h-4 w-4" /></Button>
            </div>
            {skills.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {skills.map((s) => (
                  <span key={s} className="flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs">
                    {s}
                    <button type="button" onClick={() => setSkills((arr) => arr.filter((x) => x !== s))} aria-label="Remove">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Experience</Label>
              <Button type="button" size="sm" variant="ghost" onClick={() => setExperience((arr) => [...arr, { role: "", company: "", start: "", end: "", bullets: [""] }])} data-testid="cv-add-experience">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add role
              </Button>
            </div>
            {experience.map((e, i) => (
              <div key={i} className="space-y-2 rounded-xl border border-border bg-background p-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input placeholder="Role" value={e.role} onChange={(ev) => updateExp(i, "role", ev.target.value)} className="h-10" data-testid={`cv-exp-${i}-role`} />
                  <Input placeholder="Company" value={e.company} onChange={(ev) => updateExp(i, "company", ev.target.value)} className="h-10" data-testid={`cv-exp-${i}-company`} />
                  <Input placeholder="Start (e.g. Jan 2023)" value={e.start} onChange={(ev) => updateExp(i, "start", ev.target.value)} className="h-10" />
                  <Input placeholder="End (or blank = Present)" value={e.end} onChange={(ev) => updateExp(i, "end", ev.target.value)} className="h-10" />
                </div>
                {e.bullets.map((b, bi) => (
                  <Input key={bi} placeholder={`Achievement ${bi + 1}`} value={b} onChange={(ev) => updateBullet(i, bi, ev.target.value)} className="h-9" />
                ))}
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="ghost" onClick={() => setExperience((arr) => arr.map((x, idx) => idx === i ? { ...x, bullets: [...x.bullets, ""] } : x))}>+ bullet</Button>
                  {experience.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setExperience((arr) => arr.filter((_, idx) => idx !== i))}>Remove role</Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Education</Label>
              <Button type="button" size="sm" variant="ghost" onClick={() => setEducation((arr) => [...arr, ""])}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Add
              </Button>
            </div>
            {education.map((ed, i) => (
              <Input key={i} placeholder="BSc Computer Science, University of Lagos (2020)" value={ed} onChange={(e) => setEducation((arr) => arr.map((x, idx) => idx === i ? e.target.value : x))} className="h-10" />
            ))}
          </div>

          <Button type="submit" disabled={loading} className="h-11 w-full" data-testid="cv-submit">
            <Sparkles className="mr-2 h-4 w-4" /> {loading ? "Generating…" : "Generate CV"}
          </Button>
        </form>

        <div className="space-y-3" data-testid="cv-output-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl"><FileText className="mr-2 inline h-4 w-4" /> Output</h2>
            {output && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copy} data-testid="cv-copy"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
                <Button size="sm" onClick={() => download("pdf")} data-testid="cv-download">
                  {isPro ? <Download className="mr-1 h-3.5 w-3.5" /> : <Lock className="mr-1 h-3.5 w-3.5 text-amber-400" />}
                  {isPro ? "PDF" : "Unlock download"}
                </Button>
                {isPro && (
                  <Button size="sm" variant="outline" onClick={() => download("docx")} data-testid="cv-download-docx">
                    <Download className="mr-1 h-3.5 w-3.5" /> DOCX
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 min-h-[400px]">
            {output ? (
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{output.content}</pre>
            ) : (
              <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center text-muted-foreground">
                <FileText className="h-8 w-8 opacity-40" />
                <p className="mt-3 text-sm">Your CV will appear here.</p>
              </div>
            )}
          </div>
          {output && (
            <>
              {!isPro && (
                <button onClick={() => setPaywallOpen(true)} data-testid="cv-pro-nudge"
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 text-left transition-colors hover:border-amber-400">
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium text-amber-400"><Sparkles className="h-4 w-4" /> Your CV is ready — don't let it sit here</span>
                    <span className="mt-1 block text-xs text-muted-foreground">Unlock PDF & DOCX download and apply today. Most popular Pro feature.</span>
                  </span>
                  <span className="shrink-0 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black">Unlock</span>
                </button>
              )}
              <JobPlatformPanel role={form.target_role} location={form.location} />
              <Button variant="ghost" onClick={() => nav("/documents")} className="w-full text-sm">View all saved documents →</Button>
            </>
          )}
        </div>
      </div>
      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        kind="cv_download_attempt"
        title="Your CV is ready. Unlock the download."
        body="Your ATS-friendly CV is written and saved. Unlock PDF & DOCX downloads — CV downloads are Zuntria's most popular Pro feature."
        highlights={["Download this CV as PDF & DOCX", "Unlimited CV versions for different roles", "Cover letters + all Pro tools included"]}
      />
    </div>
  );
}

function JobPlatformPanel({ role, location }) {
  const [links, setLinks] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get(`/integrations/job-boards/links?role=${encodeURIComponent(role || "")}&location=${encodeURIComponent(location || "")}`)
      .then(({ data }) => setLinks(data.links))
      .catch(() => setLinks([]));
  }, [role, location]);
  if (!links?.length) return null;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="job-platform-panel">
      <div className="flex items-center gap-2">
        <Briefcase className="h-4 w-4 text-primary" />
        <h3 className="font-display text-base">Continue to a job platform</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Download your CV above, then continue to a job board pre-searched for "{role || "your role"}". You review and submit the application yourself — Zuntria prepares, you stay in control.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {links.map((l) => (
          <Button key={l.name} size="sm" variant="outline" data-testid={`job-board-${l.name.toLowerCase()}`}
            onClick={() => {
              api.post("/paywall/hit", { kind: "job_board_click", context: l.name }).catch(() => {});
              window.open(l.url, "_blank");
              toast("Opened " + l.name + " — applying?", {
                action: { label: "Track it", onClick: () => nav(`/applications?role=${encodeURIComponent(role || "")}&platform=${encodeURIComponent(l.name)}`) },
              });
            }}>
            {l.name} <ExternalLink className="ml-1 h-3 w-3" />
          </Button>
        ))}
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" data-testid={testid} />
    </div>
  );
}
function FieldTextarea({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} />
    </div>
  );
}
